import { Modal, Form, Input, InputNumber, TreeSelect, Radio, AutoComplete, Alert, DatePicker, Select, App } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useState, useEffect } from 'react'
import { createTenant, getDatabaseGroupCodes, getDatabaseServers, listTenants, type DatabaseServer, type TenantLicense } from '../services/tenant-api'
import { markTenantRequestCompleted } from '../../tenant-requests/services/tenant-request-api'
import { assignOwnerToTenant } from '../../../services/login-api'
import { useAuth } from '../../../auth/AuthProvider'
import { useFeatureFlagSelect } from '../hooks/useFeatureFlagSelect'
import { fallbackDatabaseGroupOptions, workspaceTypeOptions } from '../../../constants/tenant.constants'
import type { TenantRequest } from '../../tenant-requests/types/tenant-request.types'

interface CreateTenantModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  /** Optional: if provided, modal operates in "from request" mode */
  request?: TenantRequest | null
}

interface CreateTenantFormValues {
  tenantName: string
  tenantCode?: string
  tenantCodePrefix?: string
  workspaceCode: string
  countryCode: string
  databaseGroupCode: string
  databaseServerCode?: string
  timeZoneId: string
  featureFlags: string[]
  workspaceType: 'new' | 'demo' | 'trial' | 'uat'
  ownerEmail: string
  inactiveDate: Dayjs | null
  // License counts
  empCount?: number
  empPsgCount?: number
  empLveCount?: number
  empClaCount?: number
  empAttCount?: number
  empPfmCount?: number
}

// Mirror Host.Data.Constants.WorkspaceType.TENANT_CODE_SUFFIX
const TENANT_CODE_SUFFIX: Record<'new' | 'demo' | 'trial' | 'uat', string> = {
  new: '01',
  demo: 'dm',
  trial: 'tr',
  uat: 'ua'
}

/**
 * Generate the next tenant code for a partner workspace.
 * NEW: <2-char partner prefix><3-digit running number>  e.g. a8001, a8002, ...
 * Ancillary (demo/trial/uat): <2-char partner prefix><2-char type suffix><1-digit running>
 *   e.g. demo: a8dm1, a8dm2, ...; trial: a8tr1, ...; uat: a8ua1, ...
 */
function generatePartnerTenantCode(
  partnerCode: string,
  workspaceType: 'new' | 'demo' | 'trial' | 'uat',
  existingTenantCodes: string[]
): string {
  const prefix = partnerCode.substring(0, 2).toLowerCase()

  if (workspaceType === 'new') {
    const matches = existingTenantCodes.filter(code =>
      code.length === 5 && code.startsWith(prefix) && /^\d{3}$/.test(code.substring(2))
    )
    let maxNumber = 0
    matches.forEach(code => {
      const num = parseInt(code.substring(2), 10)
      if (num > maxNumber) maxNumber = num
    })
    return prefix + (maxNumber + 1).toString().padStart(3, '0')
  }

  const typeSuffix = TENANT_CODE_SUFFIX[workspaceType]
  const typedPrefix = prefix + typeSuffix
  const matches = existingTenantCodes.filter(code =>
    code.length === 5 && code.startsWith(typedPrefix) && /^\d$/.test(code.substring(4))
  )
  let maxNumber = 0
  matches.forEach(code => {
    const num = parseInt(code.substring(4), 10)
    if (num > maxNumber) maxNumber = num
  })
  return typedPrefix + (maxNumber + 1).toString()
}

export function CreateTenantModal({ open, onClose, onSuccess, request }: CreateTenantModalProps) {
  const { message } = App.useApp()
  const { user } = useAuth()
  const [form] = Form.useForm<CreateTenantFormValues>()
  const { treeData } = useFeatureFlagSelect()
  const [loading, setLoading] = useState(false)
  const [databaseGroupOptions, setDatabaseGroupOptions] = useState(fallbackDatabaseGroupOptions)
  const [databaseServers, setDatabaseServers] = useState<DatabaseServer[]>([])
  const [existingTenantCodes, setExistingTenantCodes] = useState<string[]>([])

  // Determine if we're in "from request" mode
  const isFromRequest = !!request

  useEffect(() => {
    // Fetch database group codes when modal opens
    if (open) {
      getDatabaseGroupCodes()
        .then(codes => {
          const options = codes.map(code => ({ label: code, value: code }))
          setDatabaseGroupOptions(options.length > 0 ? options : fallbackDatabaseGroupOptions)
        })
        .catch(error => {
          console.error('Failed to fetch database group codes:', error)
          setDatabaseGroupOptions(fallbackDatabaseGroupOptions)
        })

      getDatabaseServers()
        .then(servers => {
          setDatabaseServers(servers)
          const defaultServer = servers.find(s => s.isDefault)
          if (defaultServer) {
            form.setFieldsValue({ databaseServerCode: defaultServer.serverCode })
          }
        })
        .catch(error => {
          console.error('Failed to fetch database servers:', error)
          setDatabaseServers([])
        })

      // Fetch existing tenant codes when in "from request" mode (for partner code generation)
      if (isFromRequest) {
        listTenants({ includeInactive: true, includeFeatureFlags: false, includeUserCount: false })
          .then(response => {
            const codes = response.data.map(tenant => tenant.tenantCode.toLowerCase())
            setExistingTenantCodes(codes)
          })
          .catch(error => {
            console.error('Failed to fetch existing tenant codes:', error)
            setExistingTenantCodes([])
          })
      } else {
        // Set owner email to current user's email when not from request
        if (user?.profile?.email) {
          form.setFieldsValue({
            ownerEmail: user.profile.email
          })
        }
      }
    }
  }, [open, user, form, isFromRequest])

  useEffect(() => {
    // Populate form when request changes (only in "from request" mode)
    if (open && request) {
      const hasPartnerCode = !!request.partnerCode

      // Determine databaseGroupCode and tenantCode based on partnerCode
      let databaseGroupCode: string
      let tenantCode = request.tenantCode
      let tenantName = request.workspaceName

      if (hasPartnerCode) {
        // Partner workspace: databaseGroupCode is the 3-letter partner code
        databaseGroupCode = request.partnerCode!.toLowerCase()

        if (!tenantCode) {
          // Generate tenant code from partner prefix (+ type suffix if not 'new') + running number
          tenantCode = generatePartnerTenantCode(
            request.partnerCode!,
            request.workspaceType || 'new',
            existingTenantCodes
          )
        }
      } else {
        // Direct workspace: databaseGroupCode is workspace_<first_letter_of_tenant_code>
        const firstLetter = (tenantCode || tenantName || 'x').charAt(0).toLowerCase()
        databaseGroupCode = `workspace_${firstLetter}`
      }

      const license: TenantLicense = request.licenseJsonb ? JSON.parse(request.licenseJsonb) : {}

      form.setFieldsValue({
        tenantName: request.workspaceName,
        tenantCode: tenantCode,
        workspaceCode: request.workspaceCode,
        countryCode: 'sg',
        databaseGroupCode: databaseGroupCode,
        timeZoneId: 'Asia/Singapore',
        featureFlags: request.featureFlags || [],
        workspaceType: request.workspaceType || 'new',
        inactiveDate: request.deactivationDate ? dayjs(request.deactivationDate) : null,
        ownerEmail: request.ownerEmail,
        empCount: license.EmpCount,
        empPsgCount: license.EmpPsgCount,
        empLveCount: license.EmpLveCount,
        empClaCount: license.EmpClaCount,
        empAttCount: license.EmpAttCount,
        empPfmCount: license.EmpPfmCount
      })
    }
  }, [open, request, form, existingTenantCodes])

  const handleSubmit = async (values: CreateTenantFormValues) => {
    // In "from request" mode, request must exist
    if (isFromRequest && !request) return

    try {
      setLoading(true)

      // Map workspaceType to restoreFromTemplate and isDemo.
      // Demo and Trial both seed from the demo template (zea02); New and UAT from zea03.
      // Only Demo is flagged as IsDemo on the tenant — Trial/UAT are real workspaces.
      const isDemo = values.workspaceType === 'demo'
      const restoreFromTemplate =
        values.workspaceType === 'demo' || values.workspaceType === 'trial' ? 'zea02' : 'zea03'

      const license: TenantLicense = {
        EmpCount: values.empCount,
        EmpPsgCount: values.empPsgCount,
        EmpLveCount: values.empLveCount,
        EmpClaCount: values.empClaCount,
        EmpAttCount: values.empAttCount,
        EmpPfmCount: values.empPfmCount
      }
      const hasLicense = Object.values(license).some(v => v !== undefined && v !== null)

      const createResponse = await createTenant({
        tenantName: values.tenantName,
        tenantCode: values.tenantCode || undefined,
        tenantCodePrefix: values.tenantCodePrefix?.toLowerCase() || undefined,
        workspaceType: values.workspaceType,
        partnerCode: request?.partnerCode || undefined,
        workspaceCode: values.workspaceCode,
        countryCode: values.countryCode,
        databaseGroupCode: values.databaseGroupCode || 'public',
        databaseServerCode: values.databaseServerCode || undefined,
        timeZoneId: values.timeZoneId,
        featureFlags: values.featureFlags || [],
        restoreFromTemplate: restoreFromTemplate,
        isDemo: isDemo,
        inactiveDate:
          (values.workspaceType === 'trial' || values.workspaceType === 'uat') && values.inactiveDate
            ? values.inactiveDate.format('YYYY-MM-DD')
            : null,
        licenseJsonb: hasLicense ? JSON.stringify(license) : undefined
      })

      // Assign owner to the tenant. The backend always returns the resolved tenantCode
      // (whether the operator supplied it or it was auto-derived).
      const resolvedTenantCode = createResponse.tenantCode || values.tenantCode || ''
      try {
        await assignOwnerToTenant({
          email: values.ownerEmail,
          loginName: values.tenantName,
          tenantCode: resolvedTenantCode
        })
      } catch (assignError: any) {
        console.error('Failed to assign owner to tenant:', assignError)
        message.warning('Workspace created but failed to assign owner. Please assign manually.')
      }

      // Mark the request as completed (only in "from request" mode)
      if (isFromRequest && request) {
        await markTenantRequestCompleted(request.id)
        message.success('Workspace created successfully and request marked as completed')
      } else {
        message.success('Workspace created successfully')
      }

      form.resetFields()
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error creating workspace:', error)
      const responseData = error.response?.data
      if (responseData?.ErrorMessage) {
        message.error(responseData.ErrorMessage)
      } else if (responseData?.error?.message) {
        message.error(responseData.error.message)
      } else {
        message.error(responseData?.message || 'Failed to create workspace. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      title={isFromRequest ? 'Create workspace from request' : 'Create new workspace'}
      open={open}
      onOk={() => form.submit()}
      onCancel={handleCancel}
      okText={isFromRequest ? 'Create workspace' : 'Create'}
      confirmLoading={loading}
      width={900}
      destroyOnHidden
    >
      {isFromRequest && request?.copyFromWorkspaceCode && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Manual restore required"
          description={
            <>
              This is a UAT workspace. Before marking the request as completed, run{' '}
              <code>pg_restore</code> from production workspace:{' '}
              <strong>{request.copyFromWorkspaceCode}</strong>. See <em>docs/NewWorkspace.md</em> for steps.
            </>
          }
        />
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          countryCode: 'sg',
          timeZoneId: 'Asia/Singapore',
          databaseGroupCode: 'public',
          workspaceType: 'new',
          featureFlags: [],
          inactiveDate: null
        }}
      >
        <Form.Item
          label="Workspace type"
          name="workspaceType"
          rules={[{ required: true, message: 'Please select workspace type' }]}
        >
          <Radio.Group>
            {workspaceTypeOptions.map(opt => (
              <Radio key={opt.value} value={opt.value}>{opt.label}</Radio>
            ))}
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Workspace name"
          name="tenantName"
          rules={[
            { required: true, message: 'Please enter workspace name' },
            { min: 3, message: 'Name must be at least 3 characters' },
            { max: 100, message: 'Name must not exceed 100 characters' }
          ]}
        >
          <Input placeholder="e.g., Zealys Pte Ltd" />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            label="Tenant code"
            name="tenantCode"
            normalize={value => value?.toLowerCase()}
            rules={[
              {
                pattern: /^[a-z0-9_-]+$/,
                message: 'Only lowercase letters, numbers, hyphens and underscores allowed'
              },
              { len: 5, message: 'Code must be exactly 5 characters' }
            ]}
            extra="Leave blank to auto-derive (5 chars, lowercase)"
          >
            <Input placeholder="auto" maxLength={5} />
          </Form.Item>

          <Form.Item
            label="Workspace code"
            name="workspaceCode"
            normalize={value => value?.toUpperCase()}
            rules={[
              { required: true, message: 'Please enter workspace code' },
              {
                pattern: /^[A-Z0-9_-]+$/,
                message: 'Only uppercase letters, numbers, hyphens and underscores allowed'
              },
              { min: 2, message: 'Code must be at least 2 characters' },
              { max: 20, message: 'Code must not exceed 20 characters' }
            ]}
            extra="Short display code (uppercase)"
          >
            <Input placeholder="e.g., ZEALYS" />
          </Form.Item>
        </div>

        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) =>
            prev.tenantCode !== cur.tenantCode ||
            prev.tenantCodePrefix !== cur.tenantCodePrefix ||
            prev.workspaceType !== cur.workspaceType
          }
        >
          {({ getFieldValue }) => {
            const tenantCode = getFieldValue('tenantCode') as string | undefined
            if (tenantCode) return null
            const hasPartner = isFromRequest && !!request?.partnerCode
            if (hasPartner) return null // partner branch is already handled client-side in the from-request flow
            const prefix = (getFieldValue('tenantCodePrefix') as string | undefined)?.toLowerCase() || ''
            const workspaceType = getFieldValue('workspaceType') as 'new' | 'demo' | 'trial' | 'uat' | undefined
            const suffix = workspaceType ? TENANT_CODE_SUFFIX[workspaceType] : '01'
            const preview = /^[a-z0-9]{3}$/.test(prefix) ? prefix + suffix : ''
            return (
              <Form.Item
                label="Tenant code prefix"
                name="tenantCodePrefix"
                normalize={value => value?.toLowerCase()}
                rules={[
                  { len: 3, message: 'Prefix must be exactly 3 characters' },
                  { pattern: /^[a-z0-9]{3}$/, message: 'Only lowercase letters and numbers' }
                ]}
                extra={preview ? `Will be derived as: ${preview}` : 'Enter 3 chars when tenant code is blank'}
              >
                <Input placeholder="e.g., zea" maxLength={3} />
              </Form.Item>
            )
          }}
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Form.Item
            label="Database group code"
            name="databaseGroupCode"
            rules={[
              { required: true, message: 'Please enter or select a database group code' },
              {
                pattern: /^[a-z0-9_-]+$/,
                message: 'Only lowercase letters, numbers, hyphens and underscores allowed'
              }
            ]}
            extra="Enter a custom value or select from list (lowercase)"
          >
            <AutoComplete
              placeholder="Enter or select database group code"
              options={databaseGroupOptions}
              filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            />
          </Form.Item>

          <Form.Item
            label="Database server"
            name="databaseServerCode"
            extra="Leave empty to use default server"
          >
            <Select
              placeholder="Default server"
              allowClear
              options={databaseServers.filter(s => s.isActive).map(s => ({
                label: `${s.serverCode} (${s.serverHost}:${s.serverPort})${s.isDefault ? ' — default' : ''}`,
                value: s.serverCode
              }))}
            />
          </Form.Item>
        </div>

        {/* Country field - hardcoded to 'sg' (Singapore) */}
        <Form.Item name="countryCode" hidden>
          <Input type="hidden" />
        </Form.Item>

        {/* Timezone field - hardcoded to 'Asia/Singapore' */}
        <Form.Item name="timeZoneId" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item
          label="Owner email"
          name="ownerEmail"
          rules={[
            { required: true, message: 'Please enter owner email' },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
          extra={isFromRequest ? 'From request' : 'Email of the workspace owner'}
        >
          <Input placeholder="e.g., owner@zealys.com" type="email" disabled={isFromRequest} />
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, cur) => prev.workspaceType !== cur.workspaceType}
        >
          {({ getFieldValue }) => {
            const workspaceType = getFieldValue('workspaceType') as 'new' | 'demo' | 'trial' | 'uat' | undefined
            if (workspaceType !== 'trial' && workspaceType !== 'uat') return null
            return (
              <Form.Item
                label="Deactivation date"
                name="inactiveDate"
                rules={[{ required: true, message: 'Deactivation date is required for Trial/UAT' }]}
                extra={isFromRequest ? 'Pre-filled from the request — override if needed' : 'Workspace becomes inactive on this date'}
              >
                <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
              </Form.Item>
            )
          }}
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.featureFlags !== currentValues.featureFlags}
        >
          {({ getFieldValue }) => {
            const featureFlags: string[] = getFieldValue('featureFlags') || []
            const hasFlag = (flag: string) => featureFlags.includes(flag)

            return (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', alignItems: 'start' }}>
                <Form.Item label="Feature flags" name="featureFlags" style={{ marginBottom: 0 }}>
                  <TreeSelect
                    treeCheckable
                    treeData={treeData}
                    placeholder="Select feature flags"
                    treeDefaultExpandAll
                    allowClear
                    showCheckedStrategy={TreeSelect.SHOW_ALL}
                  />
                </Form.Item>
                <Form.Item label="License" style={{ marginBottom: 20 }}>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '80px 1fr 130px 1fr',
                      gap: '4px 8px',
                      alignItems: 'center'
                    }}
                  >
                    <span>People</span>
                    <Form.Item name="empCount" noStyle>
                      <InputNumber min={0} placeholder="0" size="small" style={{ width: '100%' }} />
                    </Form.Item>
                    <span>Payroll</span>
                    <Form.Item name="empPsgCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('payroll')}
                      />
                    </Form.Item>
                    <span>Leave</span>
                    <Form.Item name="empLveCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('leave')}
                      />
                    </Form.Item>
                    <span>Claim</span>
                    <Form.Item name="empClaCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('claim')}
                      />
                    </Form.Item>
                    <span>Attendance</span>
                    <Form.Item name="empAttCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('attendance')}
                      />
                    </Form.Item>
                    <span>Performance review</span>
                    <Form.Item name="empPfmCount" noStyle>
                      <InputNumber
                        min={0}
                        placeholder="0"
                        size="small"
                        style={{ width: '100%' }}
                        disabled={!hasFlag('performance')}
                      />
                    </Form.Item>
                  </div>
                </Form.Item>
              </div>
            )
          }}
        </Form.Item>
      </Form>
    </Modal>
  )
}
